// The search box in the top bar: ⌘K / Ctrl+K (or /) to focus, arrows and Enter to pick, Esc to close.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api';
import { Icon } from '../ui';

interface Item { title: string; sub: string; url: string; internal?: boolean }
interface Group { key: string; label: string; items: Item[] }

export function SearchBox() {
  const navigate = useNavigate();
  const box = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(0);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 180); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.closest?.('input, textarea, select, [contenteditable]');
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) { e.preventDefault(); box.current?.focus(); setOpen(true); }
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, []);
  const ready = debounced.length >= 2 || /^#?\d+$/.test(debounced);
  const { data, isFetching } = useQuery({ queryKey: ['search', debounced], queryFn: () => get<{ groups: Group[] }>(`/api/search?q=${encodeURIComponent(debounced)}`), enabled: ready, staleTime: 10_000 });
  const items = ready ? (data?.groups ?? []).flatMap((g) => g.items) : [];
  useEffect(() => setPick(0), [debounced]);
  const go = (it: Item) => { setOpen(false); setQ(''); box.current?.blur(); navigate(it.url); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); box.current?.blur(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setPick((p) => Math.min(items.length - 1, p + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setPick((p) => Math.max(0, p - 1)); }
    else if (e.key === 'Enter' && items[pick]) { e.preventDefault(); go(items[pick]); }
  };
  let i = -1;
  return (
    <div className={`search ${open ? 'open' : ''}`}>
      <label className="search-field">
        <Icon name="search" className="ico sm" />
        <input ref={box} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
               onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="Search" aria-label="Search" role="combobox" aria-expanded={open && ready} />
        <kbd className="hide-sm">⌘K</kbd>
      </label>
      {open && ready && (
        <div className="search-pop" role="listbox">
          {(data?.groups ?? []).map((g) => (
            <div key={g.key}>
              <div className="search-group">{g.label}</div>
              {g.items.map((it) => {
                i += 1;
                const n = i;
                return (
                  <button key={it.url + it.title} type="button" role="option" aria-selected={n === pick} className={`search-item ${n === pick ? 'on' : ''}`}
                          onMouseDown={(e) => { e.preventDefault(); go(it); }} onMouseEnter={() => setPick(n)}>
                    <span className="small">{it.title}{it.internal && <span className="badge tiny-badge" style={{ marginLeft: 6 }}>Internal</span>}</span>
                    <span className="muted tiny">{it.sub}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {data && !data.groups.length && !isFetching && <div className="empty small" style={{ padding: 16 }}>Nothing found for "{debounced}"</div>}
          {!data && <div className="small muted" style={{ padding: 12 }}><span className="spin" /> Searching…</div>}
        </div>
      )}
    </div>
  );
}
